import type {
  ColorSetId,
  RegistryBlockPresentation,
  RegistryBlockType,
  ThemeId,
} from '../catalog/platformCatalogIds';

/** Semantic OKLCH surface + brand tokens for a theme-scoped color-set. */
export interface ColorSetTokenMap {
  bgBase: string;
  bgSurface: string;
  textMain: string;
  textMuted: string;
  brandPrimary: string;
  brandAccent: string;
}

export interface ThemeColorSetDefinition {
  id: ColorSetId;
  name: string;
  /** Optional chip subtitle — preset inventory brief. */
  brief?: string;
  tokens: ColorSetTokenMap;
}

/**
 * Preset or project `custom_*` chip for Appearance / onboarding pickers.
 * Registry lists stay typed as {@link ThemeColorSetDefinition}.
 */
export interface SelectableColorSetDefinition {
  id: string;
  name: string;
  brief?: string;
  tokens: ColorSetTokenMap;
}

export const SPACING_ENUM_KEYS = {
  compact: 'compact',
  balanced: 'balanced',
  relaxed: 'relaxed',
} as const;

export const SPACING_ENUM_KEY_VALUES = [
  SPACING_ENUM_KEYS.compact,
  SPACING_ENUM_KEYS.balanced,
  SPACING_ENUM_KEYS.relaxed,
] as const;

export type SpacingEnumKey = (typeof SPACING_ENUM_KEY_VALUES)[number];

/**
 * Customer-site corner radii (rem / px literals).
 * `sm` = fields / compact chrome; `cta` = buttons & CTAs; `md` = surfaces/media;
 * `pill` = always-capsule chrome (chips); sharp → 0.
 */
export interface CornerTokenMap {
  sm: string;
  md: string;
  pill: string;
  /** Buttons / primary CTAs — true capsule only on the Pill Border-radius preset. */
  cta: string;
}

/** Shared corner-radius presets — four visually distinct choices. */
export const CORNER_ENUM_KEYS = {
  sharp: 'sharp',
  subtle: 'subtle',
  rounded: 'rounded',
  pill: 'pill',
} as const;

export const CORNER_ENUM_KEY_VALUES = [
  CORNER_ENUM_KEYS.sharp,
  CORNER_ENUM_KEYS.subtle,
  CORNER_ENUM_KEYS.rounded,
  CORNER_ENUM_KEYS.pill,
] as const;

export type CornerEnumKey = (typeof CORNER_ENUM_KEY_VALUES)[number];

/**
 * Legacy 7-way corner ids → unified presets (pre-consolidation projects / themes).
 * refined/bistro/organic/vintage were visually identical on UI previews.
 */
export const LEGACY_CORNER_ENUM_ALIASES = {
  refined: CORNER_ENUM_KEYS.rounded,
  bistro: CORNER_ENUM_KEYS.rounded,
  organic: CORNER_ENUM_KEYS.rounded,
  vintage: CORNER_ENUM_KEYS.rounded,
  noir: CORNER_ENUM_KEYS.subtle,
  street: CORNER_ENUM_KEYS.subtle,
  brutalist: CORNER_ENUM_KEYS.sharp,
} as const satisfies Record<string, CornerEnumKey>;

/** Font pair + scale — spacing stays on `spacingEnum`. */
export interface TypographyPresetTokens {
  headingFont: string;
  bodyFont: string;
  scaleFactor: number;
}

/** Shared typography presets — selectable independently of theme. */
export const TYPOGRAPHY_ENUM_KEYS = {
  editorialSerif: 'editorial-serif',
  bistroWarm: 'bistro-warm',
  counterPrecision: 'counter-precision',
  /** Vietnamese neighborhood — Latin+Vietnamese display + warm sans. */
  herbSteam: 'herb-steam',
  /** Vietnamese tiled shopfront — geometric display + VI-friendly body (not Be Vietnam headlines). */
  tileAwning: 'tile-awning',
  /** Pan-Asian court — soft serif + readable sans (not sushi mincho). */
  lanternCourt: 'lantern-court',
  /** Fine dining orangery — high-contrast news serif + airy grotesque. */
  limestoneOrangery: 'limestone-orangery',
  /** Fine dining estate — warm text serif + compact grotesque. */
  moorlandHearth: 'moorland-hearth',
  brutalGrotesk: 'brutal-grotesk',
  earthOrganic: 'earth-organic',
  neonDisplay: 'neon-display',
  vintageSerif: 'vintage-serif',
  streetBold: 'street-bold',
  /** Imbiss Currywurst rail — condensed slab + utilitarian sans (not Oswald). */
  mustardRail: 'mustard-rail',
  /** Imbiss falafel window — soft display + friendly sans. */
  tahiniWindow: 'tahini-window',
  cleanSans: 'clean-sans',
} as const;

export const TYPOGRAPHY_ENUM_KEY_VALUES = [
  TYPOGRAPHY_ENUM_KEYS.editorialSerif,
  TYPOGRAPHY_ENUM_KEYS.bistroWarm,
  TYPOGRAPHY_ENUM_KEYS.counterPrecision,
  TYPOGRAPHY_ENUM_KEYS.herbSteam,
  TYPOGRAPHY_ENUM_KEYS.tileAwning,
  TYPOGRAPHY_ENUM_KEYS.lanternCourt,
  TYPOGRAPHY_ENUM_KEYS.limestoneOrangery,
  TYPOGRAPHY_ENUM_KEYS.moorlandHearth,
  TYPOGRAPHY_ENUM_KEYS.brutalGrotesk,
  TYPOGRAPHY_ENUM_KEYS.earthOrganic,
  TYPOGRAPHY_ENUM_KEYS.neonDisplay,
  TYPOGRAPHY_ENUM_KEYS.vintageSerif,
  TYPOGRAPHY_ENUM_KEYS.streetBold,
  TYPOGRAPHY_ENUM_KEYS.mustardRail,
  TYPOGRAPHY_ENUM_KEYS.tahiniWindow,
  TYPOGRAPHY_ENUM_KEYS.cleanSans,
] as const;

export type TypographyEnumKey = (typeof TYPOGRAPHY_ENUM_KEY_VALUES)[number];

export interface ThemeRegistryEntry {
  themeId: ThemeId;
  name: string;
  /**
   * Service recipe family (pages / excludeBlocks / primaryCta).
   * Visual tokens stay on this theme entry; IA seeds follow `FAMILY_RECIPE[familyId]`.
   */
  familyId: string;
  allowed_verticals: readonly string[];
  default_colorset_id: ColorSetId;
  color_sets: ThemeColorSetDefinition[];
  /**
   * Optional A/B candidates shown in Appearance / onboarding pickers.
   * Not part of the shipped 5-set stance mix; may be promoted or dropped.
   */
  reference_color_sets?: ThemeColorSetDefinition[];
  corner_enum: CornerEnumKey;
  typography_enum: TypographyEnumKey;
  typography_tokens: TypographyPresetTokens & {
    spacing: SpacingEnumKey;
  };
  corner_tokens: CornerTokenMap;
  /**
   * Curated presentation mix for block types this theme designs for.
   * Bootstrap / theme switch apply these when the type is allowed.
   */
  blockPresentations: Partial<Record<RegistryBlockType, RegistryBlockPresentation>>;
}

/** Luminance band for configurator preview matting (dark / mid / light surfaces). */
export type SurfaceTone = 'dark' | 'light' | 'mid';

export interface ResolvedProjectTheme {
  themeId: ThemeId;
  /** Registry preset id or project `custom_*` id. */
  colorsetId: string;
  tokens: ColorSetTokenMap;
  typography: ThemeRegistryEntry['typography_tokens'];
  corner: CornerTokenMap;
  cornerEnum: CornerEnumKey;
  typographyEnum: TypographyEnumKey;
}

/** Optional project-level overrides applied on top of theme registry defaults. */
export interface ProjectThemeOverrides {
  spacingEnum?: SpacingEnumKey;
  /** Current or legacy corner id (normalized at resolve time). */
  cornerEnum?: CornerEnumKey | string;
  typographyEnum?: TypographyEnumKey;
  /** Project-owned custom palettes (Appearance). */
  customColorSets?: Array<{
    id: string;
    name: string;
    brief?: string;
    basedOnColorsetId?: ColorSetId;
    tokens: ColorSetTokenMap;
  }>;
}
